import {
  CheckCircleIcon,
  CircleNotchIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import type { DraftPurchaseOrderWorkflowState } from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';

type Node = DraftPurchaseOrderWorkflowState['nodes'][number];

const DECISION_BADGE: Record<
  Node['decision'],
  {
    label: string;
    variant: 'success' | 'destructive' | 'warning' | 'secondary' | 'outline';
  }
> = {
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  escalated: { label: 'Escalated', variant: 'warning' },
  timed_out: { label: 'Timed out', variant: 'secondary' },
  pending: { label: 'Pending', variant: 'outline' },
};

function NodeDecisionIcon({ decision }: { decision: Node['decision'] }) {
  if (decision === 'pending')
    return <CircleNotchIcon size={18} weight="bold" className="text-primary" />;
  if (decision === 'rejected')
    return <XCircleIcon size={18} weight="fill" className="text-red-600" />;
  return <CheckCircleIcon size={18} weight="fill" className="text-green-600" />;
}

function formatDecidedAt(v: string | null): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * Approval pipeline for a held draft PO — a flat list of the gates a human was
 * (or is) involved in: whom each is waiting on, and for decided ones by whom,
 * when, and with which note. Pure projection of
 * `getDraftPurchaseOrderWorkflowState` (non-approval steps, unreached gates,
 * and auto-approved gates are already omitted server-side). Read-only —
 * deciding happens in the inbox.
 */
export function PurchaseOrderWorkflowPipeline({
  state,
}: {
  state: DraftPurchaseOrderWorkflowState;
}) {
  if (!state.hasWorkflow) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        <T>
          No workflow run for this purchase order yet. The pipeline appears once
          the PO enters its approval workflow.
        </T>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-background">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-medium text-muted-foreground">
          <T>Approval pipeline</T>
        </span>
        {state.approvalsTotal > 0 ? (
          <span className="text-xs text-muted-foreground">
            <T>
              <Var>{state.approvalsDone}</Var>/<Var>{state.approvalsTotal}</Var>{' '}
              approvals
            </T>
          </span>
        ) : null}
      </div>
      {state.nodes.length === 0 ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">
          <T>
            All approval gates were auto-approved — no manual decision was
            needed.
          </T>
        </div>
      ) : (
        <ul className="divide-y">
          {state.nodes.map((node) => {
            const decision = DECISION_BADGE[node.decision];
            const approver = node.assignee ?? node.role ?? null;
            const decidedAt =
              node.decision !== 'pending'
                ? formatDecidedAt(node.decidedAt)
                : null;
            return (
              <li key={node.id} className="flex items-center gap-2.5 px-3 py-2">
                <NodeDecisionIcon decision={node.decision} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {node.label}
                    </span>
                    {node.enteredViaEscalation ? (
                      <Badge
                        variant="warning"
                        className="px-1.5 py-0 text-[0.65rem]"
                      >
                        <T context="tag on an approval gate reached via an escalation path">
                          Escalation
                        </T>
                      </Badge>
                    ) : null}
                  </div>
                  {approver ? (
                    <div className="text-xs text-muted-foreground">
                      {node.role && node.assignee
                        ? `${node.role} · ${node.assignee}`
                        : approver}
                    </div>
                  ) : null}
                  <div className="text-xs text-muted-foreground">
                    {decidedAt ? `${node.sub} · ${decidedAt}` : node.sub}
                  </div>
                  {node.comment ? (
                    <div className="mt-0.5 text-xs italic text-muted-foreground">
                      {`“${node.comment}”`}
                    </div>
                  ) : null}
                </div>
                <Badge variant={decision.variant}>{decision.label}</Badge>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
